/**
 * PostCloseLockPanel — manage one EV Post-Close loan's rate lock via the
 * `/pricing/post_close/*` routes (postCloseLockApi).
 *
 * Used in two places: the drilldown Loan Lock tab (for a post-close loan) and
 * the Lock Queue's Post-Close lane detail. It resolves the loan's Salesforce
 * Opportunity Id, loads `{ loan, activeLock }`, and drives request / confirm /
 * cancel with a live price preview. Confirm writes a Rate_Lock__c to Salesforce.
 *
 * Props:
 *   loan       — an LOS loan (or synthetic { salesforceOpportunityId, ... }).
 *   history    — optional array of this loan's lock docs (the queue passes the
 *                slice of /all_locks for this opp; the drilldown omits it).
 *   onChanged  — optional callback fired after a successful mutation.
 *   onOpenForm — optional (mode) => void. The drilldown passes it so request /
 *                confirm / cancel open in the Loan Lock SIDEBAR, the same
 *                container the pre-close tab uses. Without it (Lock Queue) the
 *                same forms (PostCloseLockForms) open in a modal.
 *
 * Permissions: underwriting / admin / capital-markets can view + request; only
 * capital markets confirms. Anyone can cancel a confirmed lock (the route is
 * not team-gated), matching the pre-close Manage lock form.
 */
import {
  ErrorOutlineOutlined,
  HistoryOutlined,
  LinkOffOutlined,
  LockOutlined,
  RefreshOutlined,
} from "@mui/icons-material";
import { Box, CircularProgress, Dialog, IconButton } from "@mui/material";
import CloseRounded from "@mui/icons-material/CloseRounded";
import { AgGridReact } from "ag-grid-react";
import { useSnackbar } from "notistack";
import isNil from "lodash/isNil";
import { useCallback, useEffect, useMemo, useState } from "react";

import AgGridTableWrapper from "../../../../components/agGrid/AgGridTableWrapper";
import {
  daysUntilDateOnly,
  formatDate,
} from "../../../../_src_shared/utils/functions/formatDate";
import { formatDollar } from "../../../../utils/numbers/formatDollar";
import { formatNumber } from "../../../../utils/numbers/formatNumber";
import { formatPercent } from "../../../../utils/numbers/formatPercent";
import { usePermissionsHook } from "../../../../hooks/usePermissionsHook";
import Txt from "../../../../components/typography/Txt";
import { formSx } from "../../../../theme/formStyles";
import { lockChannelOf } from "../../../../api/losLocksApi";
import {
  noteRateToDecimal,
  postCloseLockApi,
  postCloseProductForProgram,
  resolvePostCloseOppId,
} from "../../../../api/postCloseLockApi";
import { getLockStage, isLockCurrentlyExpired } from "./loanLockUtils";
import { ink, slate, status, teal } from "../../../../theme/colors";
import {
  POST_CLOSE_FORM_TITLES,
  POST_CLOSE_LOCK_CHANGED,
  PostCloseCancelForm,
  PostCloseConfirmForm,
  PostCloseRequestForm,
} from "./PostCloseLockForms";

/* ── design tokens (shared with the V2 Loan Lock tab look) ── */
// The Eastview color system (src/theme/colors.js): teal is the accent family,
// status pairs for amber / green / rose, slate for neutrals. Status hairlines
// are the status ink at low alpha.
const T = {
  ink: ink,
  slate: slate[600],
  muted: slate[400],
  faint: slate[300],
  line: slate[200],
  bg: slate[50],
  surface: "#FFFFFF",
  accent: teal[600],
  accentSoft: teal[50],
  accentLine: teal[200],
  green: status.success.fg,
  greenSoft: status.success.bg,
  greenLine: `${status.success.fg}40`,
  amber: status.warning.fg,
  amberSoft: status.warning.bg,
  amberLine: `${status.warning.fg}40`,
  rose: status.error.fg,
  roseSoft: status.error.bg,
  roseLine: `${status.error.fg}40`,
};
const NUM = { fontVariantNumeric: "tabular-nums" };

const TONES = {
  amber: { bg: T.amberSoft, fg: T.amber, bd: T.amberLine },
  green: { bg: T.greenSoft, fg: T.green, bd: T.greenLine },
  accent: { bg: T.accentSoft, fg: T.accent, bd: T.accentLine },
  rose: { bg: T.roseSoft, fg: T.rose, bd: T.roseLine },
  slate: { bg: slate[100], fg: T.slate, bd: T.line },
};

/* ── primitives ── */
const Card = ({ children, sx }) => (
  <Box
    sx={{
      background: T.surface,
      border: `1px solid ${T.line}`,
      borderRadius: "16px",
      p: 3,
      ...sx,
    }}
  >
    {children}
  </Box>
);

const SectionLabel = ({ children, sx }) => (
  <Box
    sx={{
      fontSize: 11.5,
      fontWeight: 700,
      color: T.muted,
      textTransform: "uppercase",
      letterSpacing: 0.7,
      mb: 1.75,
      ...sx,
    }}
  >
    {children}
  </Box>
);

const Tag = ({ tone = "slate", dot, lock, children }) => {
  const c = TONES[tone] || TONES.slate;
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.75,
        fontSize: 12,
        fontWeight: 600,
        color: c.fg,
        background: c.bg,
        border: `1px solid ${c.bd}`,
        px: 1.25,
        py: "3px",
        borderRadius: "999px",
        whiteSpace: "nowrap",
        "& svg": { fontSize: 14 },
      }}
    >
      {dot && (
        <Box
          component="span"
          sx={{ width: 6, height: 6, borderRadius: "999px", background: c.fg }}
        />
      )}
      {lock && <LockOutlined sx={{ fontSize: 14, color: c.fg }} />}
      {children}
    </Box>
  );
};

const Line = ({ label, value, num, last }) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "baseline",
      gap: 1.5,
      py: 1.1,
      borderBottom: last ? "none" : `1px solid ${T.line}`,
    }}
  >
    <Box sx={{ fontSize: 13.5, color: T.slate }}>{label}</Box>
    <Box
      sx={{
        fontSize: 14,
        fontWeight: 600,
        color: isNil(value) || value === "—" ? T.faint : T.ink,
        textAlign: "right",
        ...(num && !isNil(value) ? NUM : {}),
      }}
    >
      {isNil(value) || value === "" ? "—" : value}
    </Box>
  </Box>
);

const PrimaryBtn = ({ children, onClick, color = T.accent, disabled, sx }) => (
  <Box
    component="button"
    type="button"
    onClick={onClick}
    disabled={disabled}
    sx={{
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 1,
      background: color,
      color: "#fff",
      border: "none",
      px: 2.5,
      py: 1.4,
      borderRadius: "10px",
      fontSize: 13,
      fontWeight: 600,
      cursor: disabled ? "not-allowed" : "pointer",
      opacity: disabled ? 0.55 : 1,
      whiteSpace: "nowrap",
      "& svg": { fontSize: 16 },
      "&:hover": { filter: disabled ? "none" : "brightness(0.94)" },
      ...sx,
    }}
  >
    {children}
  </Box>
);

const GhostBtn = ({ children, onClick, disabled, component = "button", sx, ...rest }) => (
  <Box
    component={component}
    type={component === "button" ? "button" : undefined}
    onClick={onClick}
    disabled={component === "button" ? disabled : undefined}
    sx={{
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 0.75,
      background: T.surface,
      color: T.slate,
      border: `1px solid ${T.line}`,
      px: 2.5,
      py: 1.4,
      borderRadius: "10px",
      fontSize: 13,
      fontWeight: 600,
      cursor: disabled ? "not-allowed" : "pointer",
      opacity: disabled ? 0.55 : 1,
      whiteSpace: "nowrap",
      "& svg": { fontSize: 14 },
      "&:hover": { background: disabled ? T.surface : slate[50] },
      ...sx,
    }}
    {...rest}
  >
    {children}
  </Box>
);

/* ── helpers ── */
const heroPct = (v) => {
  if (isNil(v)) return null;
  const s = formatPercent(v);
  return typeof s === "string" ? s.replace("%", "") : s;
};
// Calendar days (Eastern today → expiration day); ceil-ing raw instants
// counted the rest of today as a whole day (a 30-day lock read 31).
const daysUntil = (date) => (isNil(date) ? null : daysUntilDateOnly(date));
// Lock rate (decimal): RTL coupon/buy-rate, DSCR rate_at_lock.
const lockRateOf = (lock, isRtl) =>
  isRtl ? (lock?.coupon_rate ?? lock?.lock_buy_rate) : lock?.rate_at_lock;

const STATUS_TONE = {
  Requested: "amber",
  Confirmed: "green",
  Repriced: "green",
  Cancelled: "slate",
  Superseded: "slate",
  Expired: "rose",
};

/* ═══════════════════════════════════════════
   MAIN
   ═══════════════════════════════════════════ */
const PostCloseLockPanel = ({ loan, history, onChanged, onOpenForm }) => {
  const { enqueueSnackbar } = useSnackbar();
  const { canViewLoanLock, isCapitalMarkets } = usePermissionsHook();

  const oppId = useMemo(() => resolvePostCloseOppId(loan), [loan]);

  const [ctx, setCtx] = useState(null);
  const [activeLock, setActiveLock] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notPostClose, setNotPostClose] = useState(false);
  const [modal, setModal] = useState(null); // "request" | "confirm" | "cancel"
  const [busy, setBusy] = useState(false);

  const canView = canViewLoanLock;
  const canManage = isCapitalMarkets; // confirm (cancel is open to everyone)

  const refresh = useCallback(async () => {
    if (!oppId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    setNotPostClose(false);
    try {
      const res = await postCloseLockApi.getLoan(oppId);
      setCtx(res?.loan ?? null);
      setActiveLock(res?.activeLock ?? null);
    } catch (e) {
      if (e?.response?.status === 404) setNotPostClose(true);
      else
        setError(
          e?.response?.data?.detail ||
            e?.message ||
            "Failed to load post-close lock data.",
        );
    } finally {
      setLoading(false);
    }
  }, [oppId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // A save from the Loan Lock sidebar (DrilldownSidebarPostCloseLock) reloads
  // this panel.
  useEffect(() => {
    const onChange = (e) => {
      if (!e?.detail?.oppId || e.detail.oppId === oppId) {
        refresh();
        onChanged?.();
      }
    };
    window.addEventListener(POST_CLOSE_LOCK_CHANGED, onChange);
    return () => window.removeEventListener(POST_CLOSE_LOCK_CHANGED, onChange);
  }, [oppId, refresh, onChanged]);

  // Drilldown → the Loan Lock sidebar; Lock Queue → a modal.
  const openForm = (mode) => (onOpenForm ? onOpenForm(mode) : setModal(mode));

  const stage = useMemo(() => getLockStage(activeLock), [activeLock]);
  const product =
    activeLock?.product ||
    postCloseProductForProgram(ctx?.loanProgram || loan?.loanProductType);
  const isRtl = product === "rtl";
  const expired = useMemo(() => isLockCurrentlyExpired(activeLock), [activeLock]);

  // Rate priority: the lock's own rate (decimal) → SF note rate (whole percent,
  // converted) → the LOS loan's interest rate (decimal).
  const rateValue =
    lockRateOf(activeLock, isRtl) ??
    noteRateToDecimal(ctx?.noteRate) ??
    loan?.interestRate;

  const runAction = async (fn, successMsg) => {
    setBusy(true);
    try {
      await fn();
      enqueueSnackbar(successMsg, { variant: "success" });
      setModal(null);
      await refresh();
      onChanged?.();
    } catch (e) {
      enqueueSnackbar(
        e?.response?.data?.detail || e?.message || "Action failed.",
        { variant: "error" },
      );
    } finally {
      setBusy(false);
    }
  };

  const submitRequest = ({ submission, file }) =>
    runAction(
      () => postCloseLockApi.requestLock(oppId, submission, file),
      "Lock requested.",
    );

  const submitConfirm = (body) =>
    runAction(async () => {
      const res = await postCloseLockApi.confirmLock(oppId, body);
      if (res?.action && res.action !== "pushed") {
        enqueueSnackbar(`Salesforce: ${String(res.action).replace(/_/g, " ")}`, {
          variant: "info",
        });
      }
    }, "Lock confirmed and pushed to Salesforce.");

  const submitCancel = (body) =>
    runAction(() => postCloseLockApi.cancelLock(oppId, body), "Lock cancelled.");

  /* ── guards ── */
  if (!oppId) {
    return (
      <Wrap>
        <NoticeCard
          icon={<LinkOffOutlined />}
          title="Not linked to Salesforce yet"
          body="Post-close locking is keyed by the loan's Salesforce opportunity. Sync this loan to Salesforce first, then a post-close lock can be requested here."
        />
      </Wrap>
    );
  }
  if (!canView) {
    return (
      <Wrap>
        <Card>
          <Box
            sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1, py: 6 }}
          >
            <LockOutlined sx={{ fontSize: 36, color: T.faint }} />
            <Box sx={{ color: T.slate, fontSize: 13.5 }}>
              Visible to underwriting, admin, and capital-markets roles only.
            </Box>
          </Box>
        </Card>
      </Wrap>
    );
  }
  if (loading) {
    return (
      <Wrap>
        <Card sx={{ display: "grid", placeItems: "center", py: 8, gap: 2 }}>
          <CircularProgress size={26} />
          <Box sx={{ fontSize: 13, color: T.muted }}>Loading post-close lock…</Box>
        </Card>
      </Wrap>
    );
  }
  if (notPostClose) {
    return (
      <Wrap>
        <NoticeCard
          icon={<ErrorOutlineOutlined />}
          title="Not a post-close loan"
          body="This loan isn't in the EV_Post_Close population, so it has no post-close rate lock. Post-close locks apply only to EV Post-Close opportunities."
        />
      </Wrap>
    );
  }
  if (error) {
    return (
      <Wrap>
        <Card sx={{ borderColor: T.roseLine }}>
          <Box sx={{ display: "flex", gap: 1.25, alignItems: "flex-start" }}>
            <ErrorOutlineOutlined sx={{ color: T.rose, mt: "1px" }} />
            <Box>
              <Box sx={{ fontSize: 14, fontWeight: 700, color: T.rose }}>
                Couldn’t load post-close lock
              </Box>
              <Box sx={{ fontSize: 13, color: T.slate, mt: 0.5 }}>{error}</Box>
              <Box sx={{ mt: 1.5 }}>
                <GhostBtn onClick={refresh}>
                  <RefreshOutlined /> Retry
                </GhostBtn>
              </Box>
            </Box>
          </Box>
        </Card>
      </Wrap>
    );
  }

  const days = daysUntil(activeLock?.lock_expiration_date);
  const hasHistory = Array.isArray(history) && history.length > 0;

  return (
    <Wrap>
      {/* hero / header card */}
      <Card sx={{ mb: 2.5 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 3,
            flexWrap: "wrap",
          }}
        >
          <Box>
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 1.5, flexWrap: "wrap" }}
            >
              <SectionLabel sx={{ mb: 0 }}>
                {stage === 0
                  ? "Post-close rate lock"
                  : stage === 1
                    ? "Requested rate"
                    : "Locked rate"}
              </SectionLabel>
              <Tag tone="accent" dot>
                EV Post-Close
              </Tag>
              {activeLock?.lock_status && (
                <Tag tone={STATUS_TONE[activeLock.lock_status] || "slate"} lock={stage === 2}>
                  {expired ? "Expired" : activeLock.lock_status}
                </Tag>
              )}
            </Box>
            <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
              <Box
                sx={{
                  ...NUM,
                  fontSize: 52,
                  fontWeight: 700,
                  color: stage === 0 ? T.faint : T.ink,
                  lineHeight: 1,
                }}
              >
                {heroPct(rateValue) || "—.———"}
              </Box>
              <Box sx={{ fontSize: 22, color: T.slate, fontWeight: 600 }}>%</Box>
            </Box>
            <Box sx={{ fontSize: 13.5, color: T.slate, mt: 1.25, maxWidth: 460 }}>
              {stage === 0 &&
                "No active lock. Request one to capture pricing on this post-close loan and write a Rate_Lock__c to Salesforce on confirm."}
              {stage === 1 &&
                `${
                  activeLock?.lock_request_user?.fullName
                    ? `Requested by ${activeLock.lock_request_user.fullName}`
                    : "Requested"
                }${
                  activeLock?.lock_requested_date
                    ? ` on ${formatDate(activeLock.lock_requested_date)}`
                    : ""
                }. Capital markets confirms the counterparty + expiration.`}
              {(stage === 2 || stage === 3) &&
                (expired || stage === 3
                  ? "This lock has expired. Re-lock to capture fresh pricing."
                  : days != null
                    ? `Locked. ${days} day${days === 1 ? "" : "s"} until expiration${
                        activeLock?.lock_expiration_date
                          ? ` (${formatDate(activeLock.lock_expiration_date)})`
                          : ""
                      }.`
                    : "Locked.")}
            </Box>
          </Box>

          {/* actions */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25, alignItems: "flex-end" }}>
            {(stage === 0 || stage === 3) && (
              <PrimaryBtn onClick={() => openForm(stage === 3 ? "relock" : "request")}>
                <LockOutlined /> {stage === 3 ? "Re-lock" : "Request a lock"}
              </PrimaryBtn>
            )}
            {stage === 1 &&
              (canManage ? (
                <>
                  <PrimaryBtn color={T.green} onClick={() => openForm("confirm")}>
                    Confirm lock
                  </PrimaryBtn>
                  <GhostBtn onClick={() => openForm("cancel")}>Cancel</GhostBtn>
                </>
              ) : (
                <>
                  <GhostBtn onClick={() => openForm("update")}>Update request</GhostBtn>
                  <Box sx={{ fontSize: 12, color: T.muted }}>Awaiting capital markets</Box>
                </>
              ))}
            {stage === 2 && (
              <GhostBtn onClick={() => openForm("cancel")}>Cancel lock</GhostBtn>
            )}
            <GhostBtn onClick={refresh} sx={{ px: 1.5, py: 0.75 }}>
              <RefreshOutlined /> Refresh
            </GhostBtn>
          </Box>
        </Box>

        {/* SF context strip */}
        <Box
          sx={{
            display: "flex",
            gap: { xs: 4, md: 6 },
            pt: 2.75,
            mt: 2.75,
            borderTop: `1px solid ${T.line}`,
            flexWrap: "wrap",
          }}
        >
          {[
            ["Loan Id", ctx?.loanId || loan?.loanNumber, false],
            ["Program", ctx?.loanProgram || loan?.loanProductType, false],
            [
              "Final amount",
              isNil(ctx?.finalLoanAmount)
                ? isNil(loan?.totalLoanAmount)
                  ? null
                  : formatDollar(loan.totalLoanAmount)
                : formatDollar(ctx.finalLoanAmount),
              true,
            ],
            [
              "Note rate",
              isNil(ctx?.noteRate)
                ? isNil(loan?.interestRate)
                  ? null
                  : formatPercent(loan.interestRate)
                : formatPercent(noteRateToDecimal(ctx.noteRate)),
              true,
            ],
            ["DSCR", isNil(ctx?.dscr) ? null : formatNumber(ctx.dscr), true],
            ["Stage", ctx?.stage || loan?.loanStatus, false],
          ].map(([k, v, num]) => (
            <Box key={k}>
              <Box
                sx={{
                  fontSize: 11,
                  color: T.muted,
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                }}
              >
                {k}
              </Box>
              <Box
                sx={{
                  fontSize: 14,
                  fontWeight: 600,
                  mt: 0.5,
                  color: isNil(v) ? T.faint : T.ink,
                  ...(num && !isNil(v) ? NUM : {}),
                }}
              >
                {isNil(v) || v === "" ? "—" : v}
              </Box>
            </Box>
          ))}
        </Box>
      </Card>

      {/* body: history (when supplied) + active-lock terms */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: hasHistory ? "1fr 320px" : "1fr" },
          gap: 2.5,
          alignItems: "start",
        }}
      >
        {hasHistory && <LockHistoryCard locks={history} isRtl={isRtl} />}
        <LockTermsRail lock={activeLock} stage={stage} isRtl={isRtl} />
      </Box>

      <Box
        sx={{
          display: "flex",
          gap: 1.25,
          mt: 2.5,
          background: T.amberSoft,
          border: `1px solid ${T.amberLine}`,
          borderRadius: "12px",
          p: "12px 14px",
        }}
      >
        <ErrorOutlineOutlined sx={{ fontSize: 16, color: T.amber, flexShrink: 0, mt: "1px" }} />
        <Box sx={{ fontSize: 12.5, color: T.amber, lineHeight: 1.45 }}>
          Confirming writes a Rate_Lock__c to Salesforce (idempotent). Counterparty
          and lock period map to SF only when they’re recognized picklist values —
          otherwise those SF fields are left blank and the lock still saves.
        </Box>
      </Box>

      {/* Lock Queue host only — the drilldown opens these in the sidebar. */}
      {modal && (
        <PostCloseLockDialog
          title={POST_CLOSE_FORM_TITLES[modal]}
          busy={busy}
          onClose={() => setModal(null)}
        >
          {modal === "confirm" ? (
            <PostCloseConfirmForm
              variant="dialog"
              lock={activeLock}
              busy={busy}
              onClose={() => setModal(null)}
              onSubmit={submitConfirm}
            />
          ) : modal === "cancel" ? (
            <PostCloseCancelForm
              variant="dialog"
              lock={activeLock}
              busy={busy}
              onClose={() => setModal(null)}
              onSubmit={submitCancel}
            />
          ) : (
            <PostCloseRequestForm
              product={product}
              ctx={ctx}
              busy={busy}
              onClose={() => setModal(null)}
              onSubmit={submitRequest}
            />
          )}
        </PostCloseLockDialog>
      )}
    </Wrap>
  );
};

/* ── layout shell + notice ── */
const Wrap = ({ children }) => (
  <Box sx={{ background: T.bg, minHeight: "100%", color: T.ink, p: { xs: 2, md: 3.5 } }}>
    <Box sx={{ maxWidth: 1180, mx: "auto" }}>{children}</Box>
  </Box>
);

const NoticeCard = ({ icon, title, body }) => (
  <Card sx={{ textAlign: "center", p: "44px 24px" }}>
    <Box
      sx={{
        width: 56,
        height: 56,
        borderRadius: "16px",
        background: slate[100],
        color: T.faint,
        display: "grid",
        placeItems: "center",
        mx: "auto",
        mb: 2,
        "& svg": { fontSize: 26 },
      }}
    >
      {icon}
    </Box>
    <Box sx={{ fontSize: 18, fontWeight: 700, mb: 0.75 }}>{title}</Box>
    <Box sx={{ mx: "auto", maxWidth: 420, fontSize: 13.5, color: T.slate }}>{body}</Box>
  </Card>
);

/* ── right rail: active lock terms ── */
const LockTermsRail = ({ lock, stage, isRtl }) => {
  if (stage === 0 || !lock) {
    return (
      <Card sx={{ p: 2.5 }}>
        <SectionLabel>Lock terms</SectionLabel>
        <Box sx={{ fontSize: 13, color: T.muted, py: 1 }}>
          No active lock. Request one to capture pricing terms.
        </Box>
      </Card>
    );
  }
  const rate = lockRateOf(lock, isRtl);
  return (
    <Card sx={{ p: 2.5 }}>
      <SectionLabel>Lock terms</SectionLabel>
      <Line label="Product" value={(lock.product || "").toUpperCase() || null} />
      <Line label="Rate" value={isNil(rate) ? null : formatPercent(rate)} num />
      {isRtl ? (
        <>
          <Line
            label="Buy rate"
            value={isNil(lock.lock_buy_rate) ? null : formatPercent(lock.lock_buy_rate)}
            num
          />
          <Line
            label="Rate spread"
            value={
              isNil(lock.lock_rate_spread)
                ? null
                : formatPercent(lock.lock_rate_spread, { signed: true })
            }
            num
          />
        </>
      ) : (
        <>
          <Line
            label="Exit price"
            value={isNil(lock.total_exit_price) ? null : formatNumber(lock.total_exit_price)}
            num
          />
          {lock.margin_tier && <Line label="Margin tier" value={lock.margin_tier} />}
          <Line
            label="Margin"
            value={isNil(lock.actual_margin) ? null : formatNumber(lock.actual_margin)}
            num
          />
        </>
      )}
      <Line label="Lock type" value={lockChannelOf(lock)} />
      <Line label="Period" value={lock.lock_period_days ? `${lock.lock_period_days} days` : null} />
      <Line
        label="Requested"
        value={lock.lock_requested_date ? formatDate(lock.lock_requested_date) : null}
      />
      <Line
        label="Confirmed"
        value={
          lock.lock_confirm_date
            ? formatDate(lock.lock_confirm_date)
            : stage === 1
              ? "Pending"
              : null
        }
      />
      <Line
        label="Expires"
        value={
          lock.lock_expiration_date
            ? formatDate(lock.lock_expiration_date)
            : stage === 1
              ? "Set on confirm"
              : null
        }
      />
      <Box sx={{ pt: 1.25 }}>
        <Box sx={{ fontSize: 13.5, color: T.slate, mb: 0.5 }}>Lock notes</Box>
        <Box
          sx={{
            fontSize: 13.5,
            color: lock.lock_notes ? T.ink : T.faint,
            whiteSpace: "pre-wrap",
            overflowWrap: "anywhere",
          }}
        >
          {lock.lock_notes || "—"}
        </Box>
      </Box>
    </Card>
  );
};

/* ── lock history (AG Grid) — queue passes the /all_locks slice for this opp ── */
const LockHistoryCard = ({ locks, isRtl }) => {
  const columnDefs = useMemo(
    () => [
      {
        headerName: "Status",
        field: "lock_status",
        minWidth: 130,
        cellRenderer: (pr) => (
          <Tag tone={STATUS_TONE[pr.value] || "slate"}>{pr.value || "—"}</Tag>
        ),
      },
      {
        headerName: "Rate",
        minWidth: 100,
        valueGetter: (pr) => lockRateOf(pr.data, isRtl),
        valueFormatter: (pr) => (isNil(pr.value) ? "—" : formatPercent(pr.value)),
      },
      {
        headerName: isRtl ? "Buy rate" : "Exit price",
        minWidth: 110,
        valueGetter: (pr) => (isRtl ? pr.data?.lock_buy_rate : pr.data?.total_exit_price),
        valueFormatter: (pr) =>
          isNil(pr.value) ? "—" : isRtl ? formatPercent(pr.value) : formatNumber(pr.value),
      },
      {
        headerName: "Requested",
        field: "lock_requested_date",
        minWidth: 130,
        valueFormatter: (pr) => (pr.value ? formatDate(pr.value) : "—"),
      },
      {
        headerName: "Expires",
        field: "lock_expiration_date",
        minWidth: 130,
        valueFormatter: (pr) => (pr.value ? formatDate(pr.value) : "—"),
      },
      {
        headerName: "By",
        minWidth: 160,
        valueGetter: (pr) =>
          pr.data?.lock_request_user?.fullName ||
          pr.data?.lock_request_user?.emailAddress ||
          pr.data?.createdBy ||
          "—",
      },
      {
        headerName: "Notes",
        minWidth: 220,
        wrapText: true,
        autoHeight: true,
        valueGetter: (pr) =>
          [
            pr.data?.lock_notes,
            pr.data?.cancellation?.reason
              ? `Cancelled: ${pr.data.cancellation.reason}`
              : null,
          ]
            .filter(Boolean)
            .join("\n") || "—",
        cellStyle: { whiteSpace: "pre-wrap", lineHeight: "1.45" },
      },
    ],
    [isRtl],
  );

  return (
    <Card sx={{ p: 0, overflow: "hidden" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 2.5,
          py: 1.75,
          borderBottom: `1px solid ${T.line}`,
          "& svg": { fontSize: 17, color: T.slate },
        }}
      >
        <HistoryOutlined />
        <Box sx={{ fontSize: 15, fontWeight: 700 }}>Lock history</Box>
        <Box sx={{ fontSize: 12.5, color: T.muted, ml: 0.5 }}>
          {locks.length} record{locks.length === 1 ? "" : "s"}
        </Box>
      </Box>
      <Box sx={{ p: 1.5 }}>
        <AgGridTableWrapper height="auto" compactTable noBorder>
          <AgGridReact
            rowData={locks}
            columnDefs={columnDefs}
            getRowId={(pr) => pr.data?._id?.$oid || pr.data?._id || pr.data?.createDateTime}
            defaultColDef={{ sortable: true, resizable: true, flex: 1 }}
            domLayout="autoHeight"
            suppressCellFocus
          />
        </AgGridTableWrapper>
      </Box>
    </Card>
  );
};

/* ═══════════════════════════════════════════
   MODAL SHELL (Lock Queue host)
   ═══════════════════════════════════════════ */
// The FORM_STYLE_GUIDE modal shell around a PostCloseLockForms form. The form
// renders its own body padding and footer.
const PostCloseLockDialog = ({ title, busy, onClose, children }) => (
  <Dialog
    open
    onClose={busy ? undefined : onClose}
    maxWidth="sm"
    fullWidth
    PaperProps={{ sx: { ...formSx.paper, maxWidth: 640 } }}
  >
    <Box sx={formSx.head}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 2,
        }}
      >
        <Txt sx={formSx.title}>{title}</Txt>
        <IconButton
          size="small"
          onClick={onClose}
          disabled={busy}
          aria-label="Close"
          sx={{ mt: "-4px", mr: "-8px" }}
        >
          <CloseRounded sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>
    </Box>
    {children}
  </Dialog>
);

export default PostCloseLockPanel;
